import { ComponentProps, PropsWithChildren, createContext, useContext } from 'react';

import { StickyScrollFade } from '@/components/atoms/StickyScrollFade/StickyScrollFade';
import { useScrollFade } from '@/hooks/useScrollFade';
import { cn } from '@/lib/utils';

type SidebarWrapperComponentProps = { className?: string };

/**
 * Provides the current scroll-fade state from a scroll container
 * (SidebarWrapperForm / SidebarWrapperOverflowContainer) down to the sticky
 * SidebarWrapperFooter, which renders the gradient fade above its action row.
 * Defaults to false so a footer used outside a scroll container simply hides it.
 */
const SidebarFadeContext = createContext(false);

export const SidebarWrapper = ({
  className = '',
  children,
}: PropsWithChildren<SidebarWrapperComponentProps>) => {
  return (
    <div className={cn('flex h-full w-full flex-col bg-background', className)}>{children}</div>
  );
};

export const SidebarWrapperTitle = ({
  children,
  className = '',
}: PropsWithChildren<SidebarWrapperComponentProps>) => {
  return (
    <div className={cn('flex h-14 shrink-0 items-center justify-between border-b px-6', className)}>
      {children}
    </div>
  );
};

export const SidebarWrapperForm = ({
  children,
  className = '',
  ...formProps
}: PropsWithChildren<ComponentProps<'form'> & { className?: string }>) => {
  const { scrollRef, showFade, onScroll } = useScrollFade<HTMLFormElement>();
  return (
    <SidebarFadeContext.Provider value={showFade}>
      <form
        {...formProps}
        ref={scrollRef}
        onScroll={onScroll}
        className={cn('flex min-h-0 flex-1 flex-col overflow-y-auto p-6 pb-0', className)}
      >
        {children}
      </form>
    </SidebarFadeContext.Provider>
  );
};

export const SidebarWrapperOverflowContainer = ({
  children,
  className = '',
}: PropsWithChildren<SidebarWrapperComponentProps>) => {
  const { scrollRef, showFade, onScroll } = useScrollFade<HTMLDivElement>();
  return (
    <SidebarFadeContext.Provider value={showFade}>
      <div
        ref={scrollRef}
        onScroll={onScroll}
        className={cn('flex min-h-0 flex-1 flex-col overflow-y-auto p-6 pb-0', className)}
      >
        {children}
      </div>
    </SidebarFadeContext.Provider>
  );
};

export const SidebarWrapperContent = ({
  children,
  className = '',
}: PropsWithChildren<SidebarWrapperComponentProps>) => {
  return <div className={cn('space-y-4', className)}>{children}</div>;
};

export const SidebarWrapperFooter = ({
  children,
  className = '',
}: PropsWithChildren<SidebarWrapperComponentProps>) => {
  const showFade = useContext(SidebarFadeContext);
  return (
    <div
      className={cn(
        'sticky bottom-0 flex shrink-0 flex-row-reverse gap-2 bg-background py-6',
        className
      )}
    >
      {/* Gradient fade above the action row so overflowing content melts into
          the footer instead of being hard-cropped. */}
      <StickyScrollFade
        show={showFade}
        direction="bottom"
        color="from-background"
        className="h-16"
      />
      {children}
    </div>
  );
};
